import { SERVICE_GRID } from "../../../constants/serviceGrid";

export const ServiceTierRow = ({
  tierIndex,
  tier,
  serviceName,
  displayValue,
  isFirstTier = false,
  isEditable = false,
  isReadOnly = false,
  onQuantityChange,
  onDecrease,
  onIncrease,
  formatCurrency,
  gridClass = SERVICE_GRID,
  showActionColumn = true,
}) => {
  if (!tier) return null;

  const tierMinQty = Number(tier.minQty) || 0;
  const tierMaxQty = tier.maxQty ? Number(tier.maxQty) : null;
  const tierPrice = Number(tier.price) || 0;
  const tierSubtotal = tierPrice * Number(displayValue);

  // Ưu tiên rangeName, không thì dùng serviceName (tier 0), không thì dùng format mặc định
  const tierName =
    tier.rangeName ||
    (tierIndex === 0
      ? serviceName
      : `${tierMinQty} - ${tierMaxQty || "∞"} dịch vụ`);

  // Calculate max quantity for tier index 1
  let maxQty = null;
  if (tierIndex === 1) {
    maxQty = tierMaxQty ? tierMaxQty - tierMinQty + 1 : null;
  }

  return (
    <div
      className={`px-4 grid ${gridClass} items-center gap-2 py-4 text-sm ${isFirstTier ? "border-b" : "border-t border-gray-100 bg-blue-50/30"
        }`}
    >
      {/* TÊN DỊCH VỤ */}
      <div>
        <span className="font-medium text-gray-800">{tierName}</span>
      </div>

      {/* ĐƠN GIÁ */}
      <div className="flex items-center gap-2">
        <span
          className={
            isFirstTier ? "text-gray-800" : "text-blue-600 font-semibold"
          }
        >
          {tierPrice.toLocaleString()} đ
        </span>
      </div>

      {/* SỐ LƯỢNG */}
      <div className="flex items-center justify-center gap-2">
        {isReadOnly || isFirstTier ? (
          // Read-only display for EstimatePopup or first tier
          <div className="w-[70px] h-9 border rounded-md flex items-center justify-center bg-gray-100 text-gray-600">
            {displayValue}
          </div>
        ) : isEditable ? (
          // Editable with +/- buttons for Quotation (non-first tiers)
          <>
            <button
              disabled={Number(displayValue) <= 1}
              onClick={onDecrease}
              className="w-9 h-9 border rounded-md text-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
            >
              −
            </button>

            <input
              type="text"
              value={displayValue}
              onChange={(e) => onQuantityChange?.(e.target.value)}
              className="w-[70px] h-9 border rounded-md text-center bg-white focus:border-blue-500 focus:outline-none"
            />

            <button
              disabled={maxQty !== null && Number(displayValue) >= maxQty}
              onClick={onIncrease}
              className="w-9 h-9 border rounded-md text-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
            >
              +
            </button>
          </>
        ) : (
          // Fallback: read-only
          <div className="w-[70px] h-9 border rounded-md flex items-center justify-center bg-gray-100 text-gray-600">
            {displayValue}
          </div>
        )}
      </div>

      {/* THÀNH TIỀN */}
      <div className="text-right font-semibold">
        <span className={isFirstTier ? "text-gray-800" : "text-blue-600"}>
          {tierSubtotal.toLocaleString()} đ
        </span>
      </div>

      {/* XOÁ - Empty column */}
      {showActionColumn && <div className="flex justify-end"></div>}
    </div>
  );
};

export const ServiceTierRows = ({
  visibleTiers,
  tiers, // Can be priceTiers or priceRanges
  serviceName,
  tierQuantities,
  tempTierQuantities,
  isEditable = false,
  isReadOnly = false,
  onQuantityChange,
  formatCurrency,
  gridClass = SERVICE_GRID,
  showActionColumn = true,
}) => {
  return (
    <>
      {visibleTiers.map((tierIndex) => {
        const tier = tiers[tierIndex];
        if (!tier) return null;

        const debounceKey = `tier-${tierIndex}`;
        const tierQtyData = tierQuantities.find(
          (tq) => tq.tierIndex === tierIndex,
        );
        const stateValue = tierQtyData?.quantity || 1;
        const displayValue =
          tempTierQuantities[debounceKey] !== undefined
            ? tempTierQuantities[debounceKey]
            : stateValue;

        const isFirstTier = tierIndex === 0;

        return (
          <ServiceTierRow
            key={tierIndex}
            tierIndex={tierIndex}
            tier={tier}
            serviceName={serviceName}
            displayValue={displayValue}
            isFirstTier={isFirstTier}
            isEditable={isEditable && !isFirstTier}
            isReadOnly={isReadOnly}
            onQuantityChange={(value) => onQuantityChange?.(tierIndex, value)}
            onDecrease={() => {
              const newQty = Number(displayValue) - 1;
              if (newQty >= 1) {
                onQuantityChange?.(tierIndex, newQty.toString());
              }
            }}
            onIncrease={() => {
              const newQty = Number(displayValue) + 1;
              onQuantityChange?.(tierIndex, newQty.toString());
            }}
            formatCurrency={formatCurrency}
            gridClass={gridClass}
            showActionColumn={showActionColumn}
          />
        );
      })}
    </>
  );
};

export const SimpleServiceRow = ({
  serviceName,
  unitPrice,
  quantity,
  formatCurrency,
  gridClass = "grid-cols-[2.5fr_1.5fr_1.5fr_1.5fr_40px]",
  showActionColumn = true,
}) => {
  const total = Number(unitPrice || 0) * Number(quantity || 1);

  return (
    <div className={`px-4 grid ${gridClass} items-center gap-2 py-4 text-sm border-b`}>
      <div>
        <span className="font-medium text-gray-800">{serviceName}</span>
      </div>
      <div className="flex items-center gap-2 text-gray-800">
        <span>{Number(unitPrice || 0).toLocaleString()} đ</span>
      </div>
      <div className="flex items-center justify-center">
        <span className="font-medium">{String(quantity).padStart(2, "0")}</span>
      </div>
      <div className="text-right font-semibold text-gray-800">
        <span>{total.toLocaleString()} đ</span>
      </div>
      {showActionColumn && <div></div>}
    </div>
  );
};

export const calculateServiceTotal = (
  visibleTiers,
  tiers,
  tierQuantities,
  customTierPrices = {},
) => {
  let total = 0;

  visibleTiers.forEach((tierIndex) => {
    const tier = tiers[tierIndex];
    if (!tier) return;

    const tierPrice =
      customTierPrices[tierIndex] !== undefined
        ? Number(customTierPrices[tierIndex])
        : Number(tier.price) || 0;

    const tierQtyData = tierQuantities.find((tq) => tq.tierIndex === tierIndex);
    const tierQty = tierQtyData?.quantity || 1;

    total += tierPrice * tierQty;
  });

  return total;
};
